/**
 * メッセージスレッド関連の型定義
 * 新しいスレッドベースのメッセージシステム用
 */

export enum ThreadStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  BLOCKED = 'blocked',
  CLOSED = 'closed',
}

export enum SenderType {
  COMPANY = 'company',
  STUDENT = 'student',
}

export enum MessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
}

export enum ReplyType {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  QUESTION = 'question',
  OTHER = 'other',
}

// メッセージスレッド（一覧表示用）
export interface MessageThread {
  id: string;
  companyId: number;
  studentId: number;
  subject: string;
  originalScoutMessageId: number;
  status: ThreadStatus;
  lastMessageAt: string;
  messageCount: number;
  studentArchived: boolean;
  studentBlocked: boolean;
  companyArchived: boolean;
  createdAt: string;
  updatedAt: string;

  // 表示用の追加情報
  company?: {
    id: number;
    name: string;
    logoUrl?: string;
    businessType?: string;
  };
  student?: {
    id: number;
    name: string;
    avatarUrl?: string;
  };
  unreadCount?: number;
  latestMessage?: {
    preview: string;
    sentAt: string;
    senderType: SenderType;
  };
}

// スレッド詳細（会話表示用）
export interface ThreadDetail {
  thread: MessageThread;
  messages: ThreadMessage[];
  canReply: boolean;
  unreadCount: number;
}

// 個別メッセージ
export interface ThreadMessage {
  id: number;
  threadId: string;
  senderType: SenderType;
  senderUserId: number;
  messageBody: string;
  replyType?: ReplyType;
  status: MessageStatus;
  sentAt: string;
  readAt?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  updatedAt: string;

  // 表示用の追加情報
  sender?: {
    name: string;          // 表示名（短縮名）
    title?: string;        // 役職・肩書き
    avatarUrl?: string;    // アバター画像URL
    fullName?: string;     // フルネーム（正式な氏名）- useMessageThreadsでフォールバック表示に使用
  };
}

// API リクエスト・レスポンス型

export interface ThreadListQuery {
  page?: number;
  limit?: number;
  sortBy?: 'lastMessageAt' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  status?: ThreadStatus;
  search?: string;
  // アーカイブ・ブロック状態フィルター
  includeArchived?: boolean; // アーカイブ済みを含むか（デフォルト: false）
  includeBlocked?: boolean; // ブロック済みを含むか（デフォルト: false）
  onlyArchived?: boolean; // アーカイブ済みのみ表示
  onlyBlocked?: boolean; // ブロック済みのみ表示
}

export interface SendMessageRequest {
  messageBody: string;
  replyType?: ReplyType;
}

export interface UpdateThreadStatusRequest {
  status: ThreadStatus;
}

// API レスポンス型

export interface ThreadListResponse {
  success: boolean;
  data: {
    threads: MessageThread[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface ThreadDetailResponse {
  success: boolean;
  data: {
    thread: MessageThread;
    messages: ThreadMessage[];
    canReply: boolean;
    unreadCount: number;
  };
}

export interface SendMessageResponse {
  success: boolean;
  data: {
    messageId: number;
    threadId: string;
    status: MessageStatus;
    sentAt: string;
  };
  message: string;
}

export interface UpdateThreadStatusResponse {
  success: boolean;
  data: {
    threadId: string;
    status: ThreadStatus;
    updatedAt: string;
  };
  message: string;
}

export interface MarkAsReadResponse {
  success: boolean;
  data: {
    threadId: string;
    markedCount: number;
    updatedAt: string;
  };
  message: string;
}

// フィルター・検索用
export interface ThreadFilters {
  search?: string; // キーワード検索
  // アーカイブ・ブロック状態フィルター
  includeArchived?: boolean; // アーカイブ済みを含むか（デフォルト: false）
  includeBlocked?: boolean; // ブロック済みを含むか（デフォルト: false）
  onlyArchived?: boolean; // アーカイブ済みのみ表示
  onlyBlocked?: boolean; // ブロック済みのみ表示
}

// UI状態管理用
export interface ThreadUIState {
  selectedThreadId: string | null;
  isLoading: boolean;
  isLoadingDetail: boolean;
  isSending: boolean;
  error: string | null;
  filters: ThreadFilters;
  sortBy: 'lastMessageAt' | 'createdAt';
  sortOrder: 'ASC' | 'DESC';
}
